import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { services } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

export const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const iconMap = {
    Globe: () => (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    Brain: () => (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    TrendingUp: () => (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    Target: () => (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    Share2: () => (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    )
  };

  const IconComponent = iconMap[service.icon];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        className="pt-32 pb-20 lg:pt-40 lg:pb-32 relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${service.color}15 0%, ${service.bgLight} 100%)`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/"
            className="inline-flex items-center text-slate-600 hover:text-emerald-600 mb-8 transition-colors duration-200 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            Retour à l'accueil
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div 
                className="inline-flex w-16 h-16 rounded-xl items-center justify-center text-white"
                style={{ background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)` }}
              >
                {IconComponent && <IconComponent />}
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                {service.title}
              </h1>

              <p className="text-xl text-slate-600 leading-relaxed">
                {service.fullDescription}
              </p>

              <Link to="/contact">
                <Button 
                  className="rounded-full px-8 py-6 text-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg"
                  style={{ 
                    background: service.color,
                    color: 'white'
                  }}
                >
                  Demander un Devis Gratuit
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img 
                src={`https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80`}
                alt={service.title}
                className="rounded-2xl shadow-2xl"
              />
              <div 
                className="absolute -bottom-6 -right-6 w-72 h-72 rounded-2xl -z-10 opacity-20"
                style={{ backgroundColor: service.color }}
              ></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Ce Que Nous Proposons
            </h2>
            <p className="text-xl text-slate-600">
              Des solutions complètes et sur-mesure pour répondre à tous vos besoins
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.features.map((feature, index) => (
              <Card 
                key={index}
                className="p-6 hover:shadow-lg transition-all duration-300 border-2 border-slate-100 hover:border-emerald-200 rounded-xl"
              >
                <div className="flex items-start space-x-3">
                  <CheckCircle2 
                    className="w-6 h-6 flex-shrink-0 mt-1" 
                    style={{ color: service.color }}
                  />
                  <span className="text-slate-700 leading-relaxed">{feature}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Les Bénéfices Pour Votre Entreprise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {service.benefits.map((benefit, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${service.color}20` }}
                >
                  <span 
                    className="text-xl font-bold"
                    style={{ color: service.color }}
                  >
                    {index + 1}
                  </span>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{benefit}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Notre Processus
            </h2>
            <p className="text-xl text-slate-600">
              Une méthodologie éprouvée pour garantir votre succès
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border-2 border-slate-100 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:no-underline hover:text-emerald-600">
                1. Analyse et Stratégie
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pt-4">
                Nous commençons par une analyse approfondie de vos besoins, votre marché et vos objectifs. 
                Ensemble, nous définissons une stratégie claire et des KPIs mesurables pour garantir le succès de votre projet.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-2 border-slate-100 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:no-underline hover:text-emerald-600">
                2. Conception et Design
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pt-4">
                Notre équipe créative conçoit des maquettes et prototypes adaptés à votre identité de marque. 
                Vous validez chaque étape avant que nous passions au développement.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-2 border-slate-100 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:no-underline hover:text-emerald-600">
                3. Développement et Tests
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pt-4">
                Développement avec les technologies les plus performantes. Tests rigoureux sur tous les 
                appareils et navigateurs pour garantir une expérience utilisateur optimale.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-2 border-slate-100 rounded-xl px-6">
              <AccordionTrigger className="text-lg font-semibold text-slate-900 hover:no-underline hover:text-emerald-600">
                4. Lancement et Optimisation
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pt-4">
                Mise en ligne de votre projet avec suivi des performances. Optimisation continue 
                basée sur les données réelles pour maximiser vos résultats.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 lg:py-32 text-white relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)`
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Prêt à Démarrer ?
          </h2>
          <p className="text-xl mb-8 leading-relaxed opacity-95">
            Contactez-nous dès aujourd'hui pour un audit gratuit et découvrez comment 
            nous pouvons faire décoller votre projet.
          </p>
          <Link to="/contact">
            <Button className="bg-white hover:bg-slate-100 rounded-full px-10 py-6 text-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg"
              style={{ color: service.color }}
            >
              Obtenir un Devis Gratuit
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white opacity-5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
      </section>
    </div>
  );
};
